import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import { Can, EmptyMessage } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";

export default function ProfileOrganizations({ occupationalProfileId }) {
  const { t } = useTranslation();
  const [assigned, setAssigned] = useState([]);
  const [available, setAvailable] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    fetchData();
  }, [occupationalProfileId]);

  function fetchData() {
    setLoading(true);
    Promise.all([
      axios.post(
        `/occupational-profiles/${occupationalProfileId}/retrieve-organizations/`,
        { page: 0, page_size: 500 }
      ),
      axios.post(`/retrieve-organizations/`, {
        scope: "active",
        page: 0,
        page_size: 500,
      }),
    ])
      .then(([assignedRes, orgsRes]) => {
        const joined = assignedRes.data.response[0]?.data || [];
        const orgs = orgsRes.data.response[0]?.data || [];
        const orgsById = Object.fromEntries(
          orgs.map((org) => [String(org.id), org])
        );
        setAssigned(
          joined.map((row) => ({
            ...row,
            organization:
              row.organization || orgsById[String(row.organization_id)] || null,
          }))
        );
        const assignedOrgIds = new Set(
          joined.map((row) => String(row.organization_id))
        );
        setAvailable(
          orgs
            .filter((org) => !assignedOrgIds.has(String(org.id)))
            .map((org) => ({
              value: String(org.id),
              label: org.name,
            }))
        );
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function addOrganization() {
    if (!selectedId) return;
    setBusyKey(`add-${selectedId}`);
    axios
      .post(`/organizations/${selectedId}/occupational-profiles/`, {
        occupational_profile_id: occupationalProfileId,
      })
      .then(() => {
        setSelectedId(null);
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function removeOrganization(join) {
    setBusyKey(`remove-${join.id}`);
    axios
      .delete(
        `/organizations/${join.organization_id}/occupational-profiles/${join.id}/`
      )
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Title order={5}>{t("cs:organization_plural")}</Title>
      <Can permission={PERMISSIONS.ORG_OCCUPATIONAL_PROFILES_MANAGE}>
        <Card>
          <Flex gap="sm" align="flex-end">
            <Select
              style={{ flex: 1 }}
              searchable
              clearable
              placeholder={t("cs:organization_singular")}
              data={available}
              value={selectedId}
              onChange={setSelectedId}
            />
            <Button
              leftSection={<IconPlus size={14} />}
              disabled={!selectedId}
              loading={busyKey === `add-${selectedId}`}
              onClick={addOrganization}
            >
              {t("add")}
            </Button>
          </Flex>
        </Card>
      </Can>

      <Stack gap="xs">
        {!assigned.length && <EmptyMessage />}
        {assigned.map((row) => {
          const org = row.organization;
          const label = org?.name || row.organization_id;
          return (
            <Card key={row.id}>
              <Flex gap="sm" align="center">
                <Text
                  fw={700}
                  style={{ flexGrow: 1 }}
                  component={org?.id ? Link : "span"}
                  to={org?.id ? `/organizations/${org.id}` : undefined}
                  c="dark"
                >
                  {label}
                </Text>
                <Can permission={PERMISSIONS.ORG_OCCUPATIONAL_PROFILES_MANAGE}>
                  <ActionIcon
                    size="sm"
                    radius="xl"
                    variant="light"
                    color="red"
                    title={t("remove")}
                    loading={busyKey === `remove-${row.id}`}
                    onClick={() => removeOrganization(row)}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Can>
              </Flex>
            </Card>
          );
        })}
      </Stack>
    </Stack>
  );
}
